import { inject } from '@angular/core';
import { Routes } from '@angular/router';
import { authGuard } from './guards/auth.guard';
import {
  companyOnlyMatch,
  domainExpertOnlyMatch,
  notCompanyMatch,
  studentOnlyMatch,
} from './guards/company-only.guard';
import { ssoGuard } from './guards/sso.guard';
import { AuthService } from './services/auth.service';

const isCompanyUser = () => inject(AuthService).user?.type === 'company';
const isDomainExpertUser = () => inject(AuthService).user?.type === 'domain_expert';
const defaultLabsRoute = () =>
  isCompanyUser() ? 'gig-marketplace' : isDomainExpertUser() ? 'bookings' : 'ai-dev-console';

export const routes: Routes = [
  {
    path: 'landing',
    title: 'VCOE - Virtual Center of Excellence',
    canActivate: [authGuard],
    canMatch: [notCompanyMatch],
    loadComponent: () =>
      import('./pages/landing-page/landing-page.component').then(
        (component) => component.LandingPageComponent,
      ),
  },
  {
    path: 'register',
    title: 'Create Account',
    loadComponent: () =>
      import('./pages/register-page/register-page.component').then(
        (component) => component.RegisterPageComponent,
      ),
  },
  {
    path: 'register/company/account',
    title: 'Create Company Account',
    loadComponent: () =>
      import('./pages/register-company-account-page/register-company-account-page.component').then(
        (component) => component.RegisterCompanyAccountPageComponent,
      ),
  },
  {
    path: 'register/company/details',
    title: 'Company Details',
    loadComponent: () =>
      import('./pages/register-company-details-page/register-company-details-page.component').then(
        (component) => component.RegisterCompanyDetailsPageComponent,
      ),
  },
  {
    path: 'register/company/documents',
    title: 'Company Documents',
    loadComponent: () =>
      import('./pages/register-company-documents-page/register-company-documents-page.component').then(
        (component) => component.RegisterCompanyDocumentsPageComponent,
      ),
  },
  {
    path: 'register/domain-expert/personal',
    title: 'Domain Expert Registration',
    loadComponent: () =>
      import('./pages/register-domain-personal-page/register-domain-personal-page.component').then(
        (component) => component.RegisterDomainPersonalPageComponent,
      ),
  },
  {
    path: 'register/domain-expert/professional',
    title: 'Domain Expert Professional Info',
    loadComponent: () =>
      import('./pages/register-domain-professional-page/register-domain-professional-page.component').then(
        (component) => component.RegisterDomainProfessionalPageComponent,
      ),
  },
  {
    path: 'register/student',
    title: 'Student Registration',
    loadComponent: () =>
      import('./pages/register-student-page/register-student-page.component').then(
        (component) => component.RegisterStudentPageComponent,
      ),
  },
  {
    path: 'sso',
    title: 'Sign In',
    loadComponent: () =>
      import('./pages/sso-page/sso-page.component').then(
        (component) => component.SsoPageComponent,
      ),
  },
  {
    path: 'viva-sso',
    title: 'Sign In',
    loadComponent: () =>
      import('./pages/viva-sso-page/viva-sso-page.component').then(
        (component) => component.VivaSSoPageComponent,
      ),
  },
  {
    path: 'jira/callback',
    title: 'Connecting Jira...',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/jira-callback-page/jira-callback-page.component').then(
        (component) => component.JiraCallbackPageComponent,
      ),
  },
  {
    path: 'google/callback',
    title: 'Connecting Google Calendar...',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/google-callback-page/google-callback-page.component').then(
        (component) => component.GoogleCallbackPageComponent,
      ),
  },
  {
    path: 'login',
    title: 'Sign In',
    loadComponent: () =>
      import('./pages/login-page/login-page.component').then(
        (component) => component.LoginPageComponent,
      ),
  },
  {
    path: 'choose-environment',
    title: 'Choose Your Environment',
    loadComponent: () =>
      import('./pages/choose-environment-page/choose-environment-page.component').then(
        (component) => component.ChooseEnvironmentPageComponent,
      ),
  },
  {
    path: 'forgot-password',
    title: 'Forgot Password',
    loadComponent: () =>
      import('./pages/forgot-password-page/forgot-password-page.component').then(
        (component) => component.ForgotPasswordPageComponent,
      ),
  },
  {
    path: 'reset-password',
    title: 'Reset Password',
    loadComponent: () =>
      import('./pages/reset-password-page/reset-password-page.component').then(
        (component) => component.ResetPasswordPageComponent,
      ),
  },
  {
    path: 'labs',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./layout/labs-layout/labs-layout.component').then(
        (component) => component.LabsLayoutComponent,
      ),
    children: [
      {
        path: 'my-lab',
        title: 'My Lab',
        canActivate: [ssoGuard],
        loadComponent: () =>
          import('./pages/my-lab-page/my-lab-page.component').then(
            (component) => component.MyLabPageComponent,
          ),
      },
      {
        path: 'details',
        title: 'Lab Details',
        canActivate: [ssoGuard],
        loadComponent: () =>
          import('./pages/lab-details-page/lab-details-page.component').then(
            (component) => component.LabDetailsPageComponent,
          ),
      },
      {
        path: 'help',
        title: 'Help & Support',
        loadComponent: () =>
          import('./pages/help-page/help-page.component').then(
            (component) => component.HelpPageComponent,
          ),
      },
      {
        path: 'student-hub',
        title: 'Student Hub',
        loadComponent: () =>
          import('./pages/student-hub-workspace-page/student-hub-workspace-page.component').then(
            (component) => component.StudentHubWorkspacePageComponent,
          ),
      },
      {
        path: 'profile',
        title: 'Profile',
        canMatch: [companyOnlyMatch],
        loadComponent: () =>
          import('./pages/lab-details-page/lab-details-page.component').then(
            (component) => component.LabDetailsPageComponent,
          ),
      },
      {
        path: 'profile',
        title: 'Profile',
        canMatch: [domainExpertOnlyMatch],
        loadComponent: () =>
          import('./pages/domain-expert-profile-page/domain-expert-profile-page.component').then(
            (component) => component.DomainExpertProfilePageComponent,
          ),
      },
      {
        path: 'profile',
        title: 'Profile',
        canMatch: [studentOnlyMatch],
        loadComponent: () =>
          import('./pages/student-profile-page/student-profile-page.component').then(
            (component) => component.StudentProfilePageComponent,
          ),
      },
      {
        path: 'details/:id',
        title: 'Lab Details',
        canActivate: [ssoGuard],
        loadComponent: () =>
          import('./pages/lab-details-page/lab-details-page.component').then(
            (component) => component.LabDetailsPageComponent,
          ),
      },
      {
        path: 'tools',
        title: 'Tools Lab',
        loadComponent: () =>
          import('./pages/tools-lab-page/tools-lab-page.component').then(
            (component) => component.ToolsLabPageComponent,
          ),
      },
      {
        path: 'ai-dev-console',
        title: 'AI Dev Console',
        loadComponent: () =>
          import('./pages/ai-dev-console-page/ai-dev-console-page.component').then(
            (component) => component.AiDevConsolePageComponent,
          ),
      },
      {
        path: 'toolkit',
        title: 'AI Toolkit',
        loadComponent: () =>
          import('./pages/business-toolkit-page/business-toolkit-page.component').then(
            (component) => component.BusinessToolkitPageComponent,
          ),
      },
      {
        path: 'jupyter',
        title: 'Jupyter Lab',
        loadComponent: () =>
          import('./pages/jupyter-lab-page/jupyter-lab-page.component').then(
            (component) => component.JupyterLabPageComponent,
          ),
      },
      {
        path: 'gig-marketplace',
        title: 'Gig Marketplace',
        canMatch: [companyOnlyMatch],
        loadComponent: () =>
          import('./pages/gig-marketplace-page/gig-marketplace-page.component').then(
            (component) => component.GigMarketplacePageComponent,
          ),
      },
      {
        path: 'gig-marketplace',
        title: 'Gig Marketplace',
        canMatch: [notCompanyMatch],
        loadComponent: () =>
          import('./pages/student-gig-marketplace-page/student-gig-marketplace-page.component').then(
            (component) => component.StudentGigMarketplacePageComponent,
          ),
      },
      {
        path: 'gig-marketplace/active',
        title: 'My Active Gigs',
        canMatch: [notCompanyMatch],
        loadComponent: () =>
          import('./pages/student-gig-marketplace-page/student-gig-marketplace-page.component').then(
            (component) => component.StudentGigMarketplacePageComponent,
          ),
        data: { view: 'active' },
      },
      {
        path: 'gig-marketplace/transactions',
        title: 'Transaction History',
        canMatch: [companyOnlyMatch],
        loadComponent: () =>
          import('./pages/gig-transactions-page/gig-transactions-page.component').then(
            (component) => component.GigTransactionsPageComponent,
          ),
      },
      {
        path: 'gig-marketplace/proposals/:id/review',
        title: 'Submission Review',
        canMatch: [companyOnlyMatch],
        loadComponent: () =>
          import('./pages/gig-submission-review-page/gig-submission-review-page.component').then(
            (component) => component.GigSubmissionReviewPageComponent,
          ),
      },
      {
        path: 'gig-marketplace/:id/submissions',
        title: 'Project Submissions',
        canMatch: [companyOnlyMatch],
        loadComponent: () =>
          import('./pages/gig-submissions-list-page/gig-submissions-list-page.component').then(
            (component) => component.GigSubmissionsListPageComponent,
          ),
      },
      {
        path: 'gig-marketplace/proposals/:id/payment-success',
        title: 'Payment Successful',
        canMatch: [companyOnlyMatch],
        loadComponent: () =>
          import('./pages/payment-success-page/payment-success-page.component').then(
            (component) => component.PaymentSuccessPageComponent,
          ),
      },
      {
        path: 'gig-marketplace/proposals/:id/payment-received',
        title: 'Payment Received',
        canMatch: [notCompanyMatch],
        loadComponent: () =>
          import('./pages/student-payment-received-page/student-payment-received-page.component').then(
            (component) => component.StudentPaymentReceivedPageComponent,
          ),
      },
      {
        path: 'gig-marketplace/proposals/:id/submission',
        title: 'Submitted Project',
        canMatch: [notCompanyMatch],
        loadComponent: () =>
          import('./pages/student-submission-detail-page/student-submission-detail-page.component').then(
            (component) => component.StudentSubmissionDetailPageComponent,
          ),
      },
      {
        path: 'gig-marketplace/earnings',
        title: 'My Earnings',
        canMatch: [notCompanyMatch],
        loadComponent: () =>
          import('./pages/student-earnings-page/student-earnings-page.component').then(
            (component) => component.StudentEarningsPageComponent,
          ),
      },
      {
        path: 'gig-marketplace/reviews',
        title: 'Reviews',
        canMatch: [notCompanyMatch],
        loadComponent: () =>
          import('./pages/student-reviews-page/student-reviews-page.component').then(
            (component) => component.StudentReviewsPageComponent,
          ),
      },
      {
        path: 'gig-marketplace/reviews',
        title: 'Reviews',
        canMatch: [companyOnlyMatch],
        loadComponent: () =>
          import('./pages/company-reviews-page/company-reviews-page.component').then(
            (component) => component.CompanyReviewsPageComponent,
          ),
      },
      {
        path: 'jobs',
        title: 'Jobs Posted',
        canMatch: [companyOnlyMatch],
        loadComponent: () =>
          import('./pages/jobs-page/jobs-page.component').then(
            (component) => component.JobsPageComponent,
          ),
      },
      {
        path: 'gig-marketplace/create',
        title: 'Create New Gig',
        canMatch: [companyOnlyMatch],
        loadComponent: () =>
          import('./pages/create-gig-page/create-gig-page.component').then(
            (component) => component.CreateGigPageComponent,
          ),
      },
      {
        path: 'jobs/create',
        title: 'Create New Job',
        canMatch: [companyOnlyMatch],
        loadComponent: () =>
          import('./pages/jobs-create-page/jobs-create-page.component').then(
            (component) => component.JobsCreatePageComponent,
          ),
      },
      {
        path: 'jobs/:id/edit',
        title: 'Edit Job',
        canMatch: [companyOnlyMatch],
        loadComponent: () =>
          import('./pages/jobs-create-page/jobs-create-page.component').then(
            (component) => component.JobsCreatePageComponent,
          ),
      },
      {
        path: 'gig-marketplace/:id/edit',
        title: 'Edit Gig',
        canMatch: [companyOnlyMatch],
        loadComponent: () =>
          import('./pages/create-gig-page/create-gig-page.component').then(
            (component) => component.CreateGigPageComponent,
          ),
      },
      {
        path: 'gig-marketplace/:id',
        title: 'Gig Detail',
        canMatch: [companyOnlyMatch],
        loadComponent: () =>
          import('./pages/gig-detail-page/gig-detail-page.component').then(
            (component) => component.GigDetailPageComponent,
          ),
      },
      {
        path: 'gig-marketplace/:id',
        title: 'Gig Detail',
        canMatch: [notCompanyMatch],
        loadComponent: () =>
          import('./pages/student-gig-detail-page/student-gig-detail-page.component').then(
            (component) => component.StudentGigDetailPageComponent,
          ),
      },
      {
        path: 'jobs/:id',
        title: 'Job Detail',
        canMatch: [companyOnlyMatch],
        loadComponent: () =>
          import('./pages/jobs-detail-page/jobs-detail-page.component').then(
            (component) => component.JobsDetailPageComponent,
          ),
      },
      {
        path: 'sme',
        title: 'SME - Subject Matter Expert',
        loadComponent: () =>
          import('./pages/domain-expert-list-page/domain-expert-list-page.component').then(
            (component) => component.DomainExpertListPageComponent,
          ),
      },
      {
        path: 'ai-innovation-center',
        title: 'AI Innovation Center',
        canMatch: [studentOnlyMatch],
        loadComponent: () =>
          import('./pages/ai-innovation-center-page/ai-innovation-center-page.component').then(
            (component) => component.AiInnovationCenterPageComponent,
          ),
      },
      {
        path: 'prompt-evals',
        title: 'Promptfoo',
        canMatch: [studentOnlyMatch],
        loadComponent: () =>
          import('./pages/prompt-evals-page/prompt-evals-page.component').then(
            (component) => component.PromptEvalsPageComponent,
          ),
      },
      {
        path: 'sme/transactions',
        title: 'Transaction History',
        loadComponent: () =>
          import('./pages/domain-expert-transactions-page/domain-expert-transactions-page.component').then(
            (component) => component.DomainExpertTransactionsPageComponent,
          ),
      },
      {
        path: 'sme/:id',
        title: 'SME Details',
        loadComponent: () =>
          import('./pages/domain-expert-page/domain-expert-page.component').then(
            (component) => component.DomainExpertPageComponent,
          ),
      },
      {
        path: 'bookings',
        title: 'My Sessions',
        loadComponent: () =>
          import('./pages/domain-expert-bookings-page/domain-expert-bookings-page.component').then(
            (component) => component.DomainExpertBookingsPageComponent,
          ),
      },
      {
        path: 'bookings/:id',
        title: 'Session Detail',
        loadComponent: () =>
          import('./pages/domain-expert-session-detail-page/domain-expert-session-detail-page.component').then(
            (component) => component.DomainExpertSessionDetailPageComponent,
          ),
      },
      {
        path: 'chat',
        title: 'Chat',
        loadComponent: () =>
          import('./pages/chat-page/chat-page.component').then(
            (component) => component.ChatPageComponent,
          ),
      },
      {
        path: 'payments',
        title: 'Payments',
        loadComponent: () =>
          import('./pages/domain-expert-payments-page/domain-expert-payments-page.component').then(
            (component) => component.DomainExpertPaymentsPageComponent,
          ),
      },
      {
        path: 'availability',
        title: 'Availability',
        data: { title: 'Availability' },
        loadComponent: () =>
          import('./pages/domain-expert-availability-page/domain-expert-availability-page.component').then(
            (component) => component.DomainExpertAvailabilityPageComponent,
          ),
      },
      {
        path: 'reviews',
        title: 'Reviews',
        data: { title: 'Reviews' },
        loadComponent: () =>
          import('./pages/domain-expert-reviews-page/domain-expert-reviews-page.component').then(
            (component) => component.DomainExpertReviewsPageComponent,
          ),
      },
      {
        path: 'jobs',
        title: 'Job Openings',
        canMatch: [notCompanyMatch],
        loadComponent: () =>
          import('./pages/job-openings-page/job-openings-page.component').then(
            (component) => component.JobOpeningsPageComponent,
          ),
      },
      {
        path: 'jobs/:id',
        title: 'Job Details',
        canMatch: [notCompanyMatch],
        loadComponent: () =>
          import('./pages/job-detail-page/job-detail-page.component').then(
            (component) => component.JobDetailPageComponent,
          ),
      },
      {
        path: 'jobs/:id/apply',
        title: 'Apply Job',
        canMatch: [notCompanyMatch],
        loadComponent: () =>
          import('./pages/apply-job-page/apply-job-page.component').then(
            (component) => component.ApplyJobPageComponent,
          ),
      },
      { path: '', pathMatch: 'full', redirectTo: () => defaultLabsRoute() },
    ],
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: () => (isCompanyUser() || isDomainExpertUser() ? `labs/${defaultLabsRoute()}` : 'landing'),
  },
  { path: '**', redirectTo: () => `labs/${defaultLabsRoute()}` },
];
